/**
 * useDataFreshness Hook
 *
 * A custom hook that tracks data freshness for React Query queries.
 * Combines online status detection with query timestamp tracking to provide
 * a unified data freshness interface.
 *
 * Features:
 * - Tracks when data was last successfully fetched
 * - Detects if data is stale based on configurable threshold
 * - Integrates with online/offline status
 * - Provides formatted relative time strings
 *
 * Usage:
 * ```tsx
 * function MyDataView() {
 *   const query = useMyQuery();
 *   const freshness = useDataFreshness({
 *     dataUpdatedAt: query.dataUpdatedAt,
 *     isFetching: query.isFetching,
 *   });
 *
 *   return (
 *     <div>
 *       <StaleDataIndicator {...freshness} onRefresh={query.refetch} />
 *       {freshness.isOffline && <OfflineBanner show />}
 *       <MyContent data={query.data} />
 *     </div>
 *   );
 * }
 * ```
 */

import { useState, useEffect, useMemo } from "react";
import { useOnlineStatus } from "./useOnlineStatus";
import {
  formatLastUpdated,
  isDataStale,
  formatRelativeTime,
} from "@/lib/formatRelativeTime";

// ============================================================================
// Types
// ============================================================================

export interface UseDataFreshnessOptions {
  /**
   * Timestamp of when the data was last successfully fetched.
   * This comes from React Query's dataUpdatedAt.
   */
  dataUpdatedAt: number | undefined;

  /**
   * Whether data is currently being fetched/refetched.
   */
  isFetching?: boolean;

  /**
   * Time in milliseconds after which data is considered stale.
   * Default: 5 minutes (300000ms)
   */
  staleAfterMs?: number;

  /**
   * Interval in milliseconds to update the formatted time string.
   * Default: 30 seconds
   */
  updateInterval?: number;
}

export interface UseDataFreshnessReturn {
  /** Whether the browser is currently online */
  isOnline: boolean;

  /** Whether the browser is currently offline */
  isOffline: boolean;

  /** Whether the data is considered stale */
  isStale: boolean;

  /** Whether any data fetching is in progress */
  isFetching: boolean;

  /** Timestamp of when data was last updated */
  dataUpdatedAt: number | undefined;

  /** Formatted string like "Last updated 5 minutes ago" */
  formattedLastUpdated: string;

  /** Short formatted string like "5m ago" */
  formattedRelativeTime: string;

  /** Whether the component should show stale/cached data warning */
  shouldShowWarning: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_STALE_AFTER_MS = 5 * 60 * 1000; // 5 minutes
const DEFAULT_UPDATE_INTERVAL_MS = 30 * 1000; // 30 seconds

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to track data freshness with online status integration.
 *
 * @param options - Configuration options
 * @returns Data freshness state and utilities
 *
 * @example
 * ```tsx
 * const query = useHomeData();
 * const freshness = useDataFreshness({
 *   dataUpdatedAt: query.dataUpdatedAt,
 *   isFetching: query.isFetching,
 *   staleAfterMs: 2 * 60 * 1000, // 2 minutes
 * });
 *
 * if (freshness.isOffline) {
 *   // Show offline UI
 * }
 *
 * if (freshness.shouldShowWarning) {
 *   // Show stale data warning
 * }
 * ```
 */
export function useDataFreshness(
  options: UseDataFreshnessOptions
): UseDataFreshnessReturn {
  const {
    dataUpdatedAt,
    isFetching = false,
    staleAfterMs = DEFAULT_STALE_AFTER_MS,
    updateInterval = DEFAULT_UPDATE_INTERVAL_MS,
  } = options;

  // Track online/offline status
  const { isOnline, isOffline } = useOnlineStatus();

  // State for formatted strings (updated periodically)
  const [formattedLastUpdated, setFormattedLastUpdated] = useState(() =>
    formatLastUpdated(dataUpdatedAt)
  );
  const [formattedRelativeTime, setFormattedRelativeTime] = useState(() =>
    dataUpdatedAt ? formatRelativeTime(dataUpdatedAt, { abbreviated: true }) : ""
  );
  const [isStale, setIsStale] = useState(() =>
    isDataStale(dataUpdatedAt, staleAfterMs)
  );

  // Update formatted strings periodically
  useEffect(() => {
    const updateDisplays = () => {
      setFormattedLastUpdated(formatLastUpdated(dataUpdatedAt));
      setFormattedRelativeTime(
        dataUpdatedAt
          ? formatRelativeTime(dataUpdatedAt, { abbreviated: true })
          : ""
      );
      setIsStale(isDataStale(dataUpdatedAt, staleAfterMs));
    };

    // Update immediately when dataUpdatedAt changes
    updateDisplays();

    // Set up interval for periodic updates
    const interval = setInterval(updateDisplays, updateInterval);

    return () => clearInterval(interval);
  }, [dataUpdatedAt, staleAfterMs, updateInterval]);

  // Compute whether to show warning (stale or offline with cached data)
  const shouldShowWarning = useMemo(() => {
    // Show warning if offline and we have cached data
    if (isOffline && dataUpdatedAt) {
      return true;
    }
    // Show warning if online but data is stale
    return isStale;
  }, [isOffline, isStale, dataUpdatedAt]);

  return {
    isOnline,
    isOffline,
    isStale,
    isFetching,
    dataUpdatedAt,
    formattedLastUpdated,
    formattedRelativeTime,
    shouldShowWarning,
  };
}

/**
 * Simplified hook for multiple queries.
 * Returns the oldest (most stale) timestamp from the provided queries.
 */
export function useMultiQueryFreshness(
  queries: Array<{ dataUpdatedAt?: number; isFetching?: boolean }>
): UseDataFreshnessReturn {
  // Find the oldest timestamp (most stale data)
  const oldestTimestamp = useMemo(() => {
    const timestamps = queries
      .map((q) => q.dataUpdatedAt)
      .filter((t): t is number => typeof t === "number");

    if (timestamps.length === 0) return undefined;
    return Math.min(...timestamps);
  }, [queries]);

  // Check if any query is fetching
  const anyFetching = queries.some((q) => q.isFetching);

  return useDataFreshness({
    dataUpdatedAt: oldestTimestamp,
    isFetching: anyFetching,
  });
}

// ============================================================================
// Export
// ============================================================================

export default useDataFreshness;
